<template>
    <div v-if="showBrowserNotification" class="browser-notification">
        <span>
            Your browser (<b>{{ userBrowserString }}</b>) is not supported. Please update to the latest browser version<br/>
        </span>
        <button class="close-btn" @click="dismissBrowserNotification">&times;</button>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import Bowser from "bowser";
import BuilderApi from "@/helpers/api/Builder";

const showBrowserNotification = ref(false);
const supportedBrowsers = ref<{ name: string; version: string }[]>([]);
const userBrowserString = ref("");

function dismissBrowserNotification() {
    showBrowserNotification.value = false;
    localStorage.setItem("browser-notification-dismissed", "1");
}

async function fetchSupportedBrowsers() {
    const builder = new BuilderApi(
        import.meta.env.VITE_CMS_DRIVER,
        import.meta.env.VITE_BUILDER_IO_API_KEY
    );
    
    try {      
        const res = await builder.fetchEntries({ 
            model: "supported-browsers",
            options: {
                limit: 100
            }
        });
        
        if (Array.isArray(res) && res.length > 0) {
            const browsersList = res.map(entry => ({
                name: entry.data?.browser || 'Unknown Browser',
                version: entry.data?.version || '0'
            }));
            
            supportedBrowsers.value = browsersList;
        }
    } catch (error) {
        console.error('Error fetching supported browsers:', error);
    }
}

function isBrowserSupported(userBrowser: { name: string; version: string }) {   
    let unsupportedBrowser = supportedBrowsers.value.find(browser => 
        browser.name.toLowerCase() === userBrowser.name.toLowerCase()
    ) === undefined;
    if (unsupportedBrowser){
        window.sentry.captureEvent({
            message: 'Unsupported browser detected',
            level: 'warning',
            user: {
                browser: userBrowser.name,
                version: userBrowser.version
            }
        });
        return true;
    }
    const supportedBrowser = supportedBrowsers.value.find(browser => 
        browser.name.toLowerCase() === userBrowser.name.toLowerCase() &&
        parseInt(userBrowser.version) >= parseInt(browser.version)
    );
    
    return supportedBrowser !== undefined;
}

onMounted(async () => {
    if (localStorage.getItem("browser-notification-dismissed")) return;
    await fetchSupportedBrowsers();
    const parsed = Bowser.getParser(window.navigator.userAgent);
    const browserName = parsed.getBrowserName();
    const browserVersion = parsed.getBrowserVersion();
    userBrowserString.value = `${browserName} ${browserVersion}`;
    if (!isBrowserSupported({ name: browserName, version: browserVersion.split(".")[0] })) {
        showBrowserNotification.value = true;
    }
});
</script>
